// Copyright (c) 2026 Norkron. All Rights Reserved.
// Proprietary and confidential. See LICENSE file.

/**
 * `<Avatar>` — the one avatar primitive every named surface renders. The core
 * visual rule it enforces:
 *   · user      → CIRCLE
 *   · agent     → SQUARE with soft corners (an AI agent reads differently at a glance)
 *   · workspace → rounded-square badge
 *   · system    → badge with the ◆ glyph
 *
 * Shape is owned by CSS (keyed off `data-actor-type`), never inline — so it is
 * theme-aware in both light and dark. When the actor has an uploaded image
 * (`avatar_url`) it renders an `<img>`; otherwise the deterministic
 * initials+color fallback (color is a stable hash of the actor identity).
 *
 * Placement: mount as the FIRST child of a name row so the avatar sits to the
 * LEFT of the name.
 */

import type { CSSProperties } from "react";
import type { ActorRef } from "../../api/types";
import { avatarFillVars, resolveActor } from "../../lib/avatar";

export type AvatarSize = "xs" | "sm" | "md" | "lg";

export interface AvatarProps {
  actor: ActorRef;
  size?: AvatarSize;
  /** Extra class names appended to the shared `.avatar` base (e.g. `feed__avatar`
   *  so an existing surface's rail sizing composes with the shared component). */
  className?: string;
}

/** The system actor renders the ◆ glyph badge (preserves the activity feed's
 *  existing system treatment). */
function isSystem(actor: ActorRef): boolean {
  return actor.type === "system";
}

export function Avatar({ actor, size = "sm", className }: AvatarProps) {
  const resolved = resolveActor(actor);
  const hasImage = resolved.avatarUrl != null && resolved.avatarUrl !== "";
  const system = isSystem(actor);

  const classes = ["avatar", className].filter(Boolean).join(" ");
  // The deterministic fallback fill (both theme stops) is only needed on the
  // fallback path; the image path clips the picture, no fill.
  const style: CSSProperties | undefined =
    hasImage || system
      ? undefined
      : (avatarFillVars(actor.type, actor.id, actor.name) as CSSProperties);

  return (
    <span
      className={classes}
      data-testid="avatar"
      data-actor-type={actor.type}
      data-size={size}
      data-avatar-fallback={hasImage ? undefined : ""}
      style={style}
      title={resolved.name || undefined}
      aria-label={resolved.name || undefined}
      role="img"
    >
      {hasImage ? (
        <img
          className="avatar__img"
          src={resolved.avatarUrl ?? undefined}
          alt={resolved.name}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="avatar__fallback" aria-hidden>
          {system ? "◆" : resolved.initials}
        </span>
      )}
    </span>
  );
}
